import { isSuccess } from '@/context/services/type';
import { useTranslation } from '@/i18n';
import { isCommonError } from '@/libs/typeguard/isError';
import { useQueryClient } from '@tanstack/react-query';
import type {
  PostAgreementAiServiceMutationError,
  UpdateAiModelTrainingSettingMutationError,
} from 'ap-openapi';
import {
  getGetApOfficeAiSettingQueryKey,
  useGetApOfficeAiSettingSuspense,
  usePostAgreementAiService,
  useUpdateAiModelTrainingSetting,
} from 'ap-openapi';
import type { AxiosResponse } from 'axios';
import { useCallback } from 'react';
import type { AiServiceSettingResponse } from './type';

export type { AiServiceSettingResponse } from './type';

export const useGetAiServiceSetting = (): AiServiceSettingResponse => {
  const { data } = useGetApOfficeAiSettingSuspense();

  return data.data;
};

export const useAgreeAiService = () => {
  const { mutateAsync: _mutateAsync, isPending } = usePostAgreementAiService();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const mutateAsync = useCallback(
    async (isModelTrainingAllowed: boolean) => {
      let response: AxiosResponse<void | PostAgreementAiServiceMutationError>;

      try {
        response = await _mutateAsync({
          data: {
            is_model_training_allowed: isModelTrainingAllowed,
          },
        });
      } catch (error) {
        throw error instanceof Error ? error : new Error(String(error));
      }

      if (isSuccess(response.status)) {
        await queryClient.invalidateQueries({
          queryKey: getGetApOfficeAiSettingQueryKey(),
        });
        return { isSuccessful: true } as const;
      }

      if (!response.data || !isCommonError(response.data)) {
        throw new Error(
          `Unexpected AI service agreement response shape: ${response.status}`
        );
      }

      const { messages } = response.data;

      return {
        isSuccessful: false,
        message: messages?.length
          ? messages.join(', ')
          : t('ai_service_setting.agreement_error'),
      } as const;
    },
    [_mutateAsync, queryClient, t]
  );

  return { isPending, mutateAsync };
};

export const useUpdateModelTrainingSetting = () => {
  const { mutateAsync: _mutateAsync, isPending } =
    useUpdateAiModelTrainingSetting();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const mutateAsync = useCallback(
    async (isAllowed: boolean) => {
      let response: AxiosResponse<void | UpdateAiModelTrainingSettingMutationError>;

      try {
        response = await _mutateAsync({
          data: {
            is_allowed: isAllowed,
          },
        });
      } catch (error) {
        throw error instanceof Error ? error : new Error(String(error));
      }

      if (isSuccess(response.status)) {
        await queryClient.invalidateQueries({
          queryKey: getGetApOfficeAiSettingQueryKey(),
        });
        return { isSuccessful: true } as const;
      }

      if (!response.data || !isCommonError(response.data)) {
        throw new Error(
          `Unexpected model training setting response shape: ${response.status}`
        );
      }

      const { messages } = response.data;

      return {
        isSuccessful: false,
        message: messages?.length
          ? messages.join(', ')
          : t('ai_service_setting.model_training.update_error'),
      } as const;
    },
    [_mutateAsync, queryClient, t]
  );

  return { isPending, mutateAsync };
};
